/* eslint-disable no-param-reassign */
import { useEffect, useState, useCallback } from 'react';

const WIDTH_MOD_ATTR = 'data-ui-floorplans-width-modded';

// used to displace the app container horizontally
// to make space for the side navigation menu in its collapsed state
function useDisplacedTargets(
    {
        targetSelectorArray,
        legacyOnlyTargetSelectorArray,
        currentLayoutSidePanelElementRefs,
        targetStyles = {},
        displacement,
        onDisplaced,
        additionalLegacyStyles = {},
        disable,
    },
    deps = [],
) {
    const [ogWidths, setOgWidths] = useState({});

    const revertTargets = useCallback(() => {
        const selectorArr = Array.isArray(targetSelectorArray)
            ? targetSelectorArray : [targetSelectorArray];

        const elements = [];
        selectorArr.forEach((sel) => {
            document.querySelectorAll(sel).forEach((el) => elements.push({ el, sel }));
        });

        const isNuiPage = document?.querySelectorAll('#root')?.length > 0;
        const is16Cols = isNuiPage && document?.querySelectorAll('.sapcnqr-layout-grid')?.length > 0;
        const is12Cols = isNuiPage && !is16Cols && (document?.querySelectorAll('.container')?.length > 0 || document?.querySelectorAll('.container-fluid')?.length > 0);

        if (!isNuiPage) {
            legacyOnlyTargetSelectorArray.forEach((sel) => {
                document.querySelectorAll(sel).forEach((el) => elements.push({ el, sel }));
            });
        }

        elements.forEach(({ el, sel }) => {
            // If wrapped by our flex wrapper, remove spacer and unwrap
            const wrapper = el.parentElement;
            if (wrapper && wrapper.dataset && wrapper.dataset.flexWrapper === 'true') {
                // remove spacer if present
                const spacer = wrapper.firstChild;
                if (spacer && spacer !== el) {
                    spacer.remove();
                }
                // move element out of wrapper
                if (wrapper.parentNode) {
                    wrapper.parentNode.insertBefore(el, wrapper);
                }
                // remove wrapper
                wrapper.remove();
            }

            // remove inline styles applied by targetStyles for this selector
            if (targetStyles && targetStyles[sel]) {
                Object.keys(targetStyles[sel]).forEach((styleKey) => {
                    el.style[styleKey] = '';
                });
            }
        });

        if (!isNuiPage || is12Cols) {
            // remove additionalLegacyStyles.styles
            if (additionalLegacyStyles.styles) {
                Object.keys(additionalLegacyStyles.styles).forEach((sel) => {
                    document.querySelectorAll(sel).forEach((el) => {
                        Object.keys(additionalLegacyStyles.styles[sel]).forEach((k) => {
                            el.style[k] = '';
                        });
                    });
                });
            }

            if (additionalLegacyStyles.conditionalStyles) {
                Object.keys(additionalLegacyStyles.conditionalStyles).forEach((sel) => {
                    document.querySelectorAll(sel).forEach((el) => {
                        Object.keys(
                            additionalLegacyStyles.conditionalStyles[sel].style,
                        ).forEach((k) => {
                            el.style[k] = '';
                        });
                    });
                });
            }

            // remove classes added/removed by additionalLegacyStyles.classes
            if (additionalLegacyStyles.classes) {
                Object.keys(additionalLegacyStyles.classes).forEach((sel) => {
                    document.querySelectorAll(sel).forEach((el) => {
                        additionalLegacyStyles.classes[sel].forEach((item) => {
                            if (item && item.className) {
                                // attempt to remove class unconditionally
                                el.classList.remove(item.className);
                            }
                        });
                    });
                });
            }
        }

        // reset any refs' inline left set by displaceTargets
        Object.keys(currentLayoutSidePanelElementRefs).forEach((key) => {
            if (currentLayoutSidePanelElementRefs[key]?.ref?.current) {
                currentLayoutSidePanelElementRefs[key].ref.current.style.left = '';
            }
        });
        // call callback
        onDisplaced?.();
    }, [
        targetSelectorArray,
        legacyOnlyTargetSelectorArray,
        targetStyles,
        additionalLegacyStyles,
        currentLayoutSidePanelElementRefs,
        onDisplaced,
    ]);

    const displaceTargets = useCallback(() => {
        const selectorArr = Array.isArray(targetSelectorArray)
            ? targetSelectorArray : [targetSelectorArray];
        const elements = [];
        selectorArr.forEach((sel) => {
            document.querySelectorAll(sel).forEach((el) => elements.push({ el, sel }));
        });

        const isNuiPage = document?.querySelectorAll('#root')?.length > 0;
        const is16Cols = isNuiPage && document?.querySelectorAll('.sapcnqr-layout-grid')?.length > 0;
        const is12Cols = isNuiPage && !is16Cols && (document?.querySelectorAll('.container')?.length > 0 || document?.querySelectorAll('.container-fluid')?.length > 0);

        // check if we're on a legcy page without #root element
        if (!isNuiPage) {
            legacyOnlyTargetSelectorArray.forEach((sel) => {
                document.querySelectorAll(sel).forEach((el) => elements.push({ el, sel }));
            });
        }

        elements.forEach(({ el, sel }, index) => {
            // If already wrapped, just update the spacer width
            if (document.getElementById(`displaced-targets-${index}`)) {
                const wrapper = document.getElementById(`displaced-targets-${index}`);
                const spacer = wrapper.firstChild;
                if (spacer && spacer !== el) {
                    spacer.style.width = displacement;
                }
                // Still apply targetStyles if present
                if (targetStyles && targetStyles[sel]) {
                    Object.assign(el.style, targetStyles[sel]);
                }
                return;
            }

            // Create wrapper div
            const wrapper = document.createElement('div');
            wrapper.style.display = 'flex';
            wrapper.dataset.flexWrapper = 'true';
            wrapper.id = `displaced-targets-${index}`;
            if (isNuiPage) {
                wrapper.style.setProperty('height', '100%');
            }

            // Create spacer div
            const spacer = document.createElement('div');
            spacer.id = 'sapcnqrSideNavSpacer';
            // flex-shrink 0 needed for content
            // that overflows the container like on trip overview page
            Object.assign(spacer.style, { width: displacement, flexShrink: 0, transition: 'var(--cnqrSideNav_Transition)' });

            // Apply targetStyles if present for this selector
            if (targetStyles && targetStyles[sel]) {
                Object.assign(el.style, targetStyles[sel]);
            }

            // Insert wrapper in DOM
            el.parentNode.insertBefore(wrapper, el);
            wrapper.appendChild(spacer);
            wrapper.appendChild(el);
        });

        if (!isNuiPage || is12Cols) {
            // add additionalLegacyStyles that are not part of displacement targets
            Object.keys(additionalLegacyStyles.styles).forEach((sel) => {
                document.querySelectorAll(sel).forEach((el) => {
                    Object.assign(el.style, additionalLegacyStyles.styles[sel]);
                });
            });

            // add conditional additionalLegacyStyles
            Object.keys(additionalLegacyStyles.conditionalStyles).forEach((sel) => {
                document.querySelectorAll(sel).forEach((el) => {
                    if (additionalLegacyStyles.conditionalStyles[sel].condition) {
                        Object.assign(
                            el.style,
                            additionalLegacyStyles.conditionalStyles[sel].style,
                        );
                    }
                });
            });

            Object.keys(additionalLegacyStyles.classes).forEach((sel) => {
                document.querySelectorAll(sel).forEach((el) => {
                    additionalLegacyStyles.classes[sel].forEach((item, i) => {
                        if (additionalLegacyStyles.classes[sel][i].condition) {
                            el.classList.add(additionalLegacyStyles.classes[sel][i].className);
                        } else {
                            el.classList.remove(
                                additionalLegacyStyles.classes[sel][i].className,
                            );
                        }
                    });
                });
            });
        }
        const setSidePanelLeft = (sidePanelEL, shouldDisplace, displacementValue) => {
            const hasOneSidePanel = document.querySelectorAll('[data-nui-layouts="layout-side-panel-open"]').length >= 1;
            const sidePanelLeft = shouldDisplace ? displacementValue : 'unset';
            sidePanelEL.style.left = hasOneSidePanel ? 'unset' : sidePanelLeft;
        };

        // Block for LayoutSidePanel displacement and width management
        Object.keys(currentLayoutSidePanelElementRefs).forEach((key) => {
            if (currentLayoutSidePanelElementRefs[key]?.ref?.current) {
                const sidePanelEL = currentLayoutSidePanelElementRefs[key].ref.current;
                const isFullScreen = currentLayoutSidePanelElementRefs[key]?.isFullscreen;
                const isOpen = currentLayoutSidePanelElementRefs[key]?.open;
                const originalCustomWidth = currentLayoutSidePanelElementRefs[key]?.originalWidth;
                // if right is defined, the LayoutSidePanel.Element has been pushed right by
                // additional elements being open
                const isRightAdjusted = sidePanelEL.style?.right;
                const shouldDisplace = (!!isFullScreen || !!isRightAdjusted) && isOpen;
                sidePanelEL.style.left = setSidePanelLeft(
                    sidePanelEL,
                    shouldDisplace,
                    displacement,
                );
                const widthModded = sidePanelEL.getAttribute(WIDTH_MOD_ATTR) === 'true';

                // TODO: there is still an issue when fullscreen happens on small screens,
                // and then the screen is large again
                // we don't have a way to reset it
                // maybe we should fire another event
                // from sizeCalculator so we know when that is happening?
                if (shouldDisplace) {
                    if (!widthModded) {
                        let originalWidth = sidePanelEL.style.width;
                        if (sidePanelEL.style.width.includes('100%')) {
                            // 900 is the default
                            // when there is no customWidth prop on LayoutSidePanel.Element
                            originalWidth = originalCustomWidth || 900;
                        }
                        setOgWidths({
                            ...ogWidths,
                            [key]: originalWidth,
                        });

                        sidePanelEL.setAttribute(WIDTH_MOD_ATTR, 'true');
                    }

                    sidePanelEL.style.setProperty('width', `calc(100% - ${displacement})`, 'important');
                // reset the width to the original if modded and we shouldn't displace anymore
                // important to check for widthModded otherwise resizing will break after toggling
                // between fullscreen
                } else {
                    if (ogWidths[key] && widthModded) {
                        sidePanelEL.style.setProperty('width', ogWidths[key]);
                        sidePanelEL.removeAttribute(WIDTH_MOD_ATTR);
                    }
                    sidePanelEL.style.left = 'unset';
                }
            }
        });
        onDisplaced?.();
    }, [
        targetSelectorArray,
        legacyOnlyTargetSelectorArray,
        targetStyles,
        displacement,
        additionalLegacyStyles,
        currentLayoutSidePanelElementRefs,
    ]);

    useEffect(() => {
        if (!targetSelectorArray || !displacement || disable) {
            // return a no-op cleanup to satisfy React hooks linting (always return a cleanup)
            revertTargets();
            return () => { };
        }

        if (document.readyState === 'interactive' || document.readyState === 'complete') {
            displaceTargets();
        } else {
            window.addEventListener('load', displaceTargets, { once: true });
        }
        return () => { };
    }, [
        targetSelectorArray,
        displacement,
        disable,
        displaceTargets,
        revertTargets,
        ...deps,
    ]);
}

export default useDisplacedTargets;
